import {useCallback, useRef} from 'react';
import styled from 'styled-components';
import type {ActivityTimelineItem_activity$key} from './__generated__/ActivityTimelineItem_activity.graphql';
import {ActivityTimelineItem} from './ActivityTimelineItem';
import {useScrollToHash} from '../hooks/useScrollToHash';
import {useHashTracker} from '../hooks/useHashTracker';
import {useRetryMessage} from '../hooks/useRetryMessage';

interface Props {
	timeline: readonly ActivityTimelineItem_activity$key[];
	onRefetch?: () => void;
}

const EmptyCard = styled.div`
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 48px 24px;
	text-align: center;
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
`;

const EmptyText = styled.p`
	margin: 0;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #64748b;
`;

const HeaderRow = styled.div`
	margin-bottom: 16px;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const HeaderTitle = styled.span`
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 600;
	color: #334155;
`;

const Legend = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const LegendItem = styled.span`
	display: flex;
	align-items: center;
	gap: 6px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.1em;
	color: #64748b;
`;

const EmailSwatch = styled.span`
	display: inline-block;
	height: 12px;
	width: 12px;
	border-radius: 2px;
	border: 1px solid #cbd5e1;
	background: #ffffff;
`;

const NoteSwatch = styled.span`
	display: inline-block;
	height: 12px;
	width: 12px;
	border-radius: 2px;
	border: 1px dashed #dcd9ff;
	background: #eeedff;
`;

const TimelineList = styled.ol`
	margin: 0;
	padding: 0;
	list-style: none;
	> li + li {
		margin-top: 16px;
	}
`;

export function ActivityTimeline({timeline, onRefetch}: Props) {
	const containerRef = useRef<HTMLOListElement>(null);
	useScrollToHash();
	useHashTracker(containerRef);
	const {retry} = useRetryMessage();

	const handleRetry = useCallback(
		async (messageId: string) => {
			await retry(messageId);
			onRefetch?.();
		},
		[retry, onRefetch]
	);

	if (timeline.length === 0) {
		return (
			<EmptyCard>
				<EmptyText>No activity yet on this ticket.</EmptyText>
			</EmptyCard>
		);
	}

	return (
		<div>
			<HeaderRow>
				<HeaderTitle>Conversation</HeaderTitle>
				<Legend>
					<LegendItem>
						<EmailSwatch />
						Email
					</LegendItem>
					<LegendItem>
						<NoteSwatch />
						Internal note
					</LegendItem>
				</Legend>
			</HeaderRow>
			<TimelineList ref={containerRef}>
				{timeline.map((item, i) => (
					<li key={i}>
						<ActivityTimelineItem activity={item} onRetry={handleRetry} />
					</li>
				))}
			</TimelineList>
		</div>
	);
}
